<template>
  <div
    id="page-container"
    v-loading="loadingBillingInfo"
    element-loading-background="white"
  >
    <div class="sourceContainer" v-if="!loadingBillingInfo">
      <div class="orderSource">
        <div class = "orderSource-text">Order from {{ provider_name }}, {{ clinic_name }}</div>
        <SimplifiedNotification v-if = "has_gut_zoomer && address_needed"
          build_in_theme = "yellow"
          class="mt-2 mb-1"
          :with_close_btn = "false"
          content = "Once payment is completed, address changes to New York State will not be allowed since some of your test is temporarily not available to residents of New York."
        />
        <SimplifiedNotification v-if = "is_family_order"
          title =  "This is a Family Discount Order"
          class="mt-2 mb-1"
          build_in_theme = "blue"
          :with_close_btn = "false"
          :content = "`Along this order, there are ${number_of_sample_ids_under_cur_family_order-1} additional orders placed in this bundle and they must be paid together for orders to be processed.`"
        />
      </div>
    </div>
    <div class="pageBodyContainer" v-if="!loadingBillingInfo">
      <PaymentOverview :payFixedAmount="payFixedAmount" />
      <div class="paymentInfo">
        <div class="paymentInfoHeader">Payment Information</div>
        <div class="headerDivider" />
        <div class="subpartContainer">
          <div class="paymentSubtitle">Payment Method</div>
          <SimplifiedNotification
            v-if="paymentMethod === 'splitit'"
            title="No new loan. No credit check. Pay with the credit card you already have"
            build_in_theme="blue"
            :with_close_btn="false"
            content="No added fees. No added interest. We'll place a hold on your card and spread out the charges."
          />
          <PaymentRadio
            v-if="!payFixedAmount"
            ref = "paymentMethodRadio"
            :options="adjusted_payment_options"
            @onChange="handlePaymentMethodChange"
            :isPaymentMethod="true"
            :preSelectValue="this.count >= 3 && enable_bnpl_method ? 'payLater' : ''"
          />
          <PaymentRadio
            v-else
            ref = "paymentMethodRadio"
            :options=" adjusted_internal_payment_options"
            @onChange="handlePaymentMethodChange"
          />
        </div>
        <BnplContainer v-if="paymentMethod == 'payLater'" />
        <SplititContainer v-if="paymentMethod == 'splitit'" :balance="current_patient_balance" />
        <!-- CardPayContainer means pay with Stax card platform -->
        <CardPayContainer v-if="paymentMethod == 'card' && !provider_has_revenue && !is_international_payment && is_currency_usd" />
        <!-- HSACardPayContainer means pay with Stripe card platform, can switch between stax and stripe-->
        <HsaCardPayContainer v-if="paymentMethod == 'card' && (provider_has_revenue || is_international_payment || !is_currency_usd)" :isHSACard="false" />
        <HsaCardPayContainer v-if="paymentMethod == 'hsa/fsa'" :isHSACard="true"/>
        <!-- <DigitalWalletContainer v-show="paymentMethod == 'digitalWallet'" /> -->
      </div>
      <div v-if = "does_patient_pay_off" class = "continue-button">
        <ResponsiveButton display_name='Continue' build_in_theme="filled-primary" 
        width_type="fill-whole"
        @click = "clickContinue" />
      </div>
      <img id="footerImage" alt="Footer Image" src="@/assets/footerImage.svg" />
    </div>
  </div>
</template>

<script>
import PaymentRadio from "@/components/payment/PaymentRadio.vue";
import PaymentOptions from "@/utils/paymentMethodOptions.js";
import InternalPaymentOptions from "@/utils/InternalPaymentMethodOptions.js";
import PaymentOverview from "@/components/payment/PaymentOverview.vue";
import BnplContainer from "@/components/payment/BnplContainer.vue";
import CardPayContainer from "@/components/payment/CardPayContainer.vue";
import HsaCardPayContainer from "@/components/payment/HsaCardPayContainer.vue";
import SplititContainer from "@/components/payment/SplititContainer.vue";
import { mapState, mapActions } from "pinia";
import { usePaymentStore } from "@/stores/PaymentStore.ts";
import { useCommonStore } from "@/stores/CommonStore.ts";
import { ElNotification } from "element-plus";

export default {
  name: "PaymentView",
  components: {
    PaymentRadio,
    PaymentOverview,
    BnplContainer,
    CardPayContainer,
    HsaCardPayContainer,
    SplititContainer,
    // DigitalWalletContainer,
  },
  async mounted(){
    window.scrollTo(0, 0);
    this.loadingBillingInfo = true;
    try{
      // If token, sample_id or accession_id in PaymentStore are null or empty, means user refresh at the current page:
      // -> need to get the query from the localStorage, and reset variables in store
      if(!this.sample_id || !this.accession_id || !this.token){
          console.log('getLocalStorageQuery PaymentView',localStorage.getItem('pns_single_page_query'));

          // 1. set data in PaymentStore: for page initiation when refresh at /payment_form
          // NOTES: need to use await, since storeAllQueryInfomation might throw Error
          await this.storeAllQueryInfomation(JSON.parse(localStorage.getItem('pns_single_page_query')));
          // 2. set data in CommonStore: for the top header information and 'pnsToken' in localStorage
          this.setAccessionId(this.accession_id);
          this.setSampleId(this.sample_id);   
          this.setJwtToken(this.token);    
      }
      // 3. Initiate and assign data needed by PaymentForm page
      await this.initiatePaymentFormPage();

      // 4. If this.direct_to_blooddraw: normally won't run under this case, since if direct_to_blooddraw, should directly jump to Blood Draw Site Page at SinglePageFlowMedia

      // 5. If no billing issue: jump to Payment Success Page
      if(!this.has_billing_issue){
        this.$router.push('/payment_succeed');
      }else{
      // 6. If has billing issue:
        //  6.1 If the provider is the ONLY payee or the patient is one of the payee and the patient has paid off the responsible balance: Jump to Order Pending Page
        if(this.is_provider_payee && (!this.is_patient_payee || (this.is_patient_payee && this.does_patient_pay_off))){
          this.$router.push('/order_pending');
        }
        //  6.2 If the patient is one of the payee, but DOESN'T pay off the responsible balance: Stay on Payment Form Page with related billing details
        //  6.3 If the patient is the ONLY payee and DOESN'T pay off the responsible balance: Stay on Payment Form Page with related billing details
        
      }
    }catch(err){
      console.log('PaymentView mounted error: ', err);
      ElNotification({
          title: 'Error',
          type: 'error',
          customClass: "errorNotification",
          message: err?.message ?? 'Failed to fetch payment form page.',
          duration: 0,
      });
      this.$router.push('/failed');
    }finally{
      this.loadingBillingInfo = false;
    }
  },
  data() {
    return {
      paymentMethod: "",
      PaymentOptions,
      InternalPaymentOptions,
      loadingBillingInfo: false,
    };
  },
  computed: {
    ...mapState(()=>usePaymentStore(), [
      'accession_id',
      'sample_id',
      'token',
      'amount_to_pay',
      'payment_prioritized',
      'direct_to_blooddraw',
      'has_billing_issue',
      'provider_has_revenue',
      'charge_method',
      'billingInfo',
      'supported_bnpl_methods_data',
      "clinic_name",
      "provider_name", 
      "count",
      'is_international_payment',
      'address_needed',
      'has_gut_zoomer',
      'is_family_order',
      'family_order_sample_ids',
    ]),
    number_of_sample_ids_under_cur_family_order(){
      // If the current order is a family order, return the number of sample IDs under the current family order
      return this.is_family_order
      ? this.family_order_sample_ids.length
      : 0;
    },
    payFixedAmount(){
    // ONLY allow to pay fixed amount, if enter from LIS
      return this.amount_to_pay ? true : false;
    },
    is_currency_usd(){
      return this.billingInfo?.currency == 'usd';
    },
    // NOTES: Provider and paitent can both be payees for the same order
    // NOTES: currently only 'customerPay' and 'netPay' for chargeMethod means provider-paid
    is_provider_payee(){
      // If the order is provider-paid, the provider definitely is the payee
      // If the order is patient-paid, the provider definitely isn't the payee
      return this.charge_method == 'customerPay' || this.charge_method == 'netPay';
    },
    is_patient_payee(){
      // If billingInfo's 'patient_bill' property is not null or undefined, the patient is the payee
      // NOTES: patient_bill is an object, if it is not null or undefined.
      return this.billingInfo?.patient_bill;
    },
    current_patient_balance(){
      // console.log('current_balance',this.billing_info && this.billing_info.total ? this.billing_info.total : 'N/A');
      return this.is_family_order ? Number(this.billingInfo?.family_orders?.patient_total ?? 0) : Number(this.billingInfo?.patient_bill?.total ?? 0);
    },
    does_patient_pay_off(){
      return this.current_patient_balance <= 0;
    },
    enable_bnpl_method(){
      return this.supported_bnpl_methods_data
      ? this.supported_bnpl_methods_data.klarna_eligible || this.supported_bnpl_methods_data.afterpay_clearpay_eligible
      : false;
    },
    adjusted_payment_options(){
      var output = [{ label: 'Credit / Debit Card' , value: 'card', ...(this.does_patient_pay_off && {disabled:true})}];

      output.push({ label: 'Payment Plan', value: 'splitit', ...(this.does_patient_pay_off && {disabled:true}) });

      if(this.enable_bnpl_method){
        output.push({ label: 'Buy Now Pay Later', value: 'payLater', ...(this.does_patient_pay_off && {disabled:true}) });
      }
      
      if(this.is_currency_usd && !this.provider_has_revenue){
        output.push ({ label: 'HSA/FSA Card', value: 'hsa/fsa', ...(this.does_patient_pay_off && {disabled:true}) });
      }
      console.log('adjusted_payment_options', output);
      return output;
    },
    adjusted_internal_payment_options(){
      var output = [{ label: 'Credit / Debit Card' , value: 'card',...(this.does_patient_pay_off && {disabled:true}) }];
      if(this.is_currency_usd && !this.provider_has_revenue){
        output.push ({ label: 'HSA/FSA Card', value: 'hsa/fsa',...(this.does_patient_pay_off && {disabled:true}) });
      }
      console.log('adjusted_internal_payment_options', output);
      return output;
    },
  },
  watch:{
    does_patient_pay_off(newVal){
      // If the patient has paid off the responsible balance, reset the payment method to be empty
      if(newVal){
        this.paymentMethod = "";
        this.$refs['paymentMethodRadio']?.resetValue();
      }
    },
    // paymentMethod(newValue){
    //   // If the payment method is changed, reset the payment form
    //   console.log('paymentMethod', newValue);
    // },
  },
  methods: {
    ...mapActions(()=>useCommonStore(),[
      'setAccessionId',
      'setSampleId',
      'setJwtToken',
    ]),
    ...mapActions(()=>usePaymentStore(), [
      'storeAllQueryInfomation',
      'initiatePaymentFormPage',
    ]),
    clickContinue(){
      this.$router.push('/payment_succeed');
    },
    handlePaymentMethodChange(value) {
      this.paymentMethod = value;
    },
  },
};
</script>

<style scoped>
/* :deep(.el-loading-spinner) {
  top: 20%;
} */
#page-container {
  min-height: calc(100vh - 77px);
}

.sourceContainer {
  display: flex;
  justify-content: center;
  width: 100%;
}
.orderSource {
  display: flex;
  flex-direction: column;
  row-gap:6px;
  
  width: 56vw;
  margin-top: 10px;
}
.orderSource-text {
  font-family: Roboto;
  font-size: 18px;
  font-weight: 400;
  line-height: 21px;
  letter-spacing: 0.52px;
  color: #2e557b;
}

.pageBodyContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 6px;
  height: 1700px;
  position: relative;
}

.paymentInfo {
  width: 56vw;
  margin-top: 30px;
  display: flex;
  flex-direction: column;
}

.paymentInfoHeader {
  font-size: 24px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0.51px;
  color: #2e557b;
}

.subpartContainer {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.headerDivider {
  border: 1px solid #bcccdc;
  margin: 16px 0;
}
.continue-button{
  width: 56vw;
}

@media only screen and (max-width: 1200px) {
  .orderSource {
    margin-top: 10px;
  }
}

@media only screen and (max-width: 1024px) {
  #footerImage {
    display: none;
  }
}

@media only screen and (max-width: 767px) {
  .orderSource {
    width: 90%;
    margin-top: 10px;
  }
  .orderSource-text {
    font-size: 12px;
  }
  .paymentInfo {
    width: 90%;
    margin-top: 20px;
  }

  .paymentInfoHeader {
    font-size: 16px;
  }

  .headerDivider {
    margin: 12px 0;
  }

  .subpartContainer {
    gap: 12px;
    margin-bottom: 12px;
  }
  .continue-button{
    width: 90%;
  }
}
</style>
